// biome-ignore format: historic
// biome-ignore format: historic
// biome-ignore format: historic
import { editedCardFieldsContext } from '@context/edited-card-fields-context';
import { PopoverContext } from '@context/popover-context';
import useNode from '@hooks/use-node';
import useOnFocus from '@hooks/use-on-focus';
import useValidateInput, { emailValidator } from '@hooks/use-validate-input';
import { getSuggestionsByFieldType } from '@modules/card-helpers';
import { Stack, TextField } from '@ui-lib';
import { useContext, useEffect } from 'react';
import type { CardEmail, CardPresetEmail } from 'types';

import LinkStatusContainer from '../LinkStatusContainer/LinkStatusContainer';
import SuggestionsSection, {
  useSuggestionsSection,
} from '../SuggestionsSection/SuggestionsSection';

interface EmailEditPopoverProps<T> {
  field: T;
  onChange: (field: T) => void;
  linked?: boolean;
  mode: 'create' | 'update' | undefined;
}

function EmailEditPopover<T extends CardEmail | CardPresetEmail>({
  field,
  onChange,
  linked = false,
}: EmailEditPopoverProps<T>): React.ReactElement {
  const [isError, errorMessage] = useValidateInput(field.value, [emailValidator]);
  const [inputNode, inputRef] = useNode<HTMLInputElement>();
  const [, , emailInputBlurred] = useOnFocus(inputNode);
  const { show } = useContext(PopoverContext);
  const { onEditAttribute } = useContext(editedCardFieldsContext);
  const options = getSuggestionsByFieldType('email')();
  const [selectedOption, setSelectedOption] = useSuggestionsSection(field.label, options);

  useEffect(() => {
    if (show && inputNode) {
      const intersectionObserver = new IntersectionObserver((entries, observer) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setTimeout(() => {
            inputNode.focus();
          }, 150);
          observer.unobserve(entry.target);
        }
      });

      intersectionObserver.observe(inputNode);
    }
  }, [show, inputNode]);

  function handleLabelChange(label: string) {
    onChange({ ...field, label });
  }

  function handleValueChange(value: string) {
    onEditAttribute(field.id);
    onChange({ ...field, value });
  }

  function handleSelectSuggestion(suggestion: string) {
    const newSelectedOption = suggestion === selectedOption ? '' : suggestion;

    setSelectedOption(newSelectedOption);
    onChange({ ...field, label: newSelectedOption });
  }

  return (
    <Stack gap="large">
      <LinkStatusContainer linked={linked}>
        <TextField
          ref={inputRef}
          value={field.value}
          onChange={(event) => handleValueChange(event.currentTarget.value)}
          label="Value"
          type="email"
          autoComplete="email"
          message={isError && emailInputBlurred ? errorMessage : undefined}
          tone={isError && emailInputBlurred ? 'critical' : 'neutral'}
        />
      </LinkStatusContainer>
      <TextField
        value={field.label}
        onChange={(event) => handleLabelChange(event.currentTarget.value)}
        label="Label (Optional)"
      />
      <SuggestionsSection
        options={options}
        onSelect={handleSelectSuggestion}
        selectedOption={selectedOption}
        subject="label"
      />
    </Stack>
  );
}

export default EmailEditPopover;
